import { TourDefinition, TourStep } from './tour.types';

// ─── 1. tour-home ─────────────────────────────────────────────────────────────
// Disparo: automático em /home quando primeiroLogin == true
// Conclusão: tourHomeConcluido + primeiroLogin=false + menuOrgPulsando=true

export const TOUR_HOME: TourDefinition = {
  id: 'tour-home',
  flagConclusao: 'tourHomeConcluido',
  visibleTo: ['*'],
  steps: [
    {
      route: '/home',
      element: '[data-tour="home-greeting"]',
      title: '👋 Bem-vindo ao Manager',
      description:
        'Este é seu painel de comando. Aqui você tem uma visão rápida do que está ' +
        'acontecendo com seu time agora mesmo — <strong>quem está online, quem está ' +
        'ausente e quantos colaboradores estão monitorados</strong>.',
      side: 'bottom',
    },
    {
      route: '/home',
      element: '[data-tour="home-stats"]',
      title: '📊 Seus números em tempo real',
      description:
        'Esses cartões são o coração do seu time. Com o agente instalado, você terá ' +
        'acesso automaticamente ao <strong>pulso do seu time</strong> para entender a ' +
        'realidade da rotina dos seus colaboradores.',
      side: 'bottom',
    },
    {
      route: '/home',
      element: '[data-tour="home-quick-actions"]',
      title: '⚡ Atalhos para as seções principais',
      description:
        'Chegue rápido a qualquer parte do iManager. <strong>Colaboradores</strong> ' +
        'para ver seu time, <strong>Relatórios</strong> para os diagnósticos semanais por IA ' +
        'e <strong>Organização</strong> para estruturar sua empresa.',
      side: 'top',
    },
    {
      route: '/home',
      element: '[data-tour="menu-organizacao"]',
      title: '🏢 O primeiro passo é aqui',
      description:
        'Antes de tudo, você precisa <strong>configurar sua organização</strong> — criar ' +
        'departamentos, cargos e times. Clique em <strong>Organização</strong> no menu ' +
        'para começar. Vamos guiar você por cada seção.',
      side: 'right',
    },
  ],
};

// ─── 2. tour-org-estrutura ────────────────────────────────────────────────────
// Disparo: automático em /empresa quando tourOrgEstruturaConcluido == false
// Forçar aba Departamentos e Times antes de iniciar

export const TOUR_ORG_ESTRUTURA: TourDefinition = {
  id: 'tour-org-estrutura',
  flagConclusao: 'tourOrgEstruturaConcluido',
  visibleTo: ['*'],
  steps: [
    {
      route: '/empresa',
      queryParams: { tab: 'departamentos-times' },
      element: '[data-tour="tab-departamentos-times"]',
      title: '🏗️ Departamentos e Times',
      description:
        'Aqui você organiza a <strong>estrutura da sua empresa</strong> — crie departamentos ' +
        'como TI, Comercial ou Financeiro e, dentro de cada um, os <strong>times que serão ' +
        'monitorados</strong>. Tudo em uma visão unificada.',
      side: 'bottom',
    },
    {
      route: '/empresa',
      queryParams: { tab: 'cargos' },
      element: '[data-tour="tab-cargos"]',
      title: '💼 Cargos — os papéis do seu time',
      description:
        'Defina os <strong>cargos existentes na empresa</strong>, como Analista, Gerente ' +
        'ou Desenvolvedor. Isso permite filtrar relatórios e entender o desempenho por ' +
        'função — não apenas por pessoa.',
      side: 'bottom',
    },
    {
      route: '/empresa',
      queryParams: { tab: 'departamentos-times' },
      element: '[data-tour="tab-departamentos-times"]',
      title: '✅ Comece pela estrutura',
      description:
        'Crie pelo menos <strong>um departamento e um time</strong>. Você pode usar ' +
        '"Geral" como departamento inicial e criar um time dentro dele. Sem times, ' +
        'não é possível monitorar colaboradores nem gerar relatórios.',
      side: 'bottom',
    },
  ],
};

// ─── 3. tour-org-times ────────────────────────────────────────────────────────
// Disparo: automático em /empresa com tab=departamentos-times quando tourOrgTimesConcluido == false
// Variantes: semTimes (2 steps) | comTimes (1 step)

const _TOUR_ORG_TIMES_STEP_INTRO: TourStep = {
  route: '/empresa',
  queryParams: { tab: 'departamentos-times' },
  element: '[data-tour="tab-departamentos-times"]',
  title: '👥 Times — o coração do monitoramento',
  description:
    '<strong>Times são grupos de colaboradores</strong> que você vai monitorar ' +
    'juntos. Expanda um departamento para ver e gerenciar os times dentro dele. ' +
    'Tudo no Manager gira em torno dos times.',
  side: 'bottom',
};

export const TOUR_ORG_TIMES: TourDefinition = {
  id: 'tour-org-times',
  flagConclusao: 'tourOrgTimesConcluido',
  visibleTo: ['*'],
  // steps base — substituídos em runtime pela variante adequada
  steps: [_TOUR_ORG_TIMES_STEP_INTRO],
  variantes: {
    semTimes: [
      _TOUR_ORG_TIMES_STEP_INTRO,
      {
        route: '/empresa',
        queryParams: { tab: 'departamentos-times' },
        element: '[data-tour="tab-departamentos-times"]',
        title: '🚀 Crie seu primeiro time agora',
        description:
          'Você ainda não tem nenhum time cadastrado. Expanda um departamento e clique ' +
          'em <strong>"Novo time"</strong>, ou use o botão <strong>"+ Time"</strong> na ' +
          'barra superior. Sem times, não é possível monitorar colaboradores.',
        side: 'bottom',
      },
    ],
    comTimes: [
      {
        route: '/empresa',
        queryParams: { tab: 'departamentos-times' },
        element: '[data-tour="tab-departamentos-times"]',
        title: '👥 Seus times estão aqui',
        description:
          'Você já tem times criados. Expanda qualquer departamento para <strong>editar, ' +
          'criar novos ou reorganizar</strong> os times. Cada time tem seu próprio painel ' +
          'de monitoramento e relatórios.',
        side: 'bottom',
      },
    ],
  },
};

// ─── 4. tour-org-hierarquia ───────────────────────────────────────────────────
// Disparo: automático em /empresa/organograma quando tourOrgHierarquiaConcluido == false
// Steps de botões admin (2 e 3) aparecem apenas se ehAdmin == true

export const TOUR_ORG_HIERARQUIA: TourDefinition = {
  id: 'tour-org-hierarquia',
  flagConclusao: 'tourOrgHierarquiaConcluido',
  visibleTo: ['*'],
  steps: [
    {
      route: '/empresa/organograma',
      element: '[data-tour="tab-hierarquia"]',
      title: '🌳 O organograma da sua empresa',
      description:
        'Esta aba mostra <strong>como sua empresa está estruturada visualmente</strong> ' +
        '— quem responde a quem, por departamento. É aqui que você enxerga a cadeia ' +
        'de liderança de um jeito claro e organizado.',
      side: 'bottom',
    },
    {
      route: '/empresa/organograma',
      element: '[data-tour="btn-gestao-pessoas"]',
      title: '👤 Adicione pessoas à hierarquia',
      description:
        'Com o botão <strong>"Novo usuário"</strong> você cadastra gestores e colaboradores e ' +
        'define o acesso deles ao portal. Cada pessoa precisa ser cadastrada aqui ' +
        'antes de aparecer nos relatórios e no monitoramento.',
      side: 'left',
    },
    {
      route: '/empresa/agent',
      element: '[data-tour="btn-download-instalador"]',
      title: '💻 Instale o agente nas máquinas',
      description:
        'O Manager precisa de um <strong>agente instalado no computador de cada ' +
        'colaborador</strong> para coletar dados de atividade. Baixe o instalador ' +
        'aqui e distribua para seu time — é fácil e simples, mas recomendamos que seja feito pelo TI especializado.',
      side: 'left',
    },
    {
      route: '/empresa/organograma',
      element: '[data-tour="tab-hierarquia"]',
      title: '✅ Estrutura pronta, agora o time',
      description:
        'Com departamentos, cargos, times e pessoas configurados, você está pronto ' +
        'para <strong>monitorar seu time de verdade</strong>. Vá em ' +
        '<strong>Pessoas</strong> no menu para ver o painel do seu time.',
      side: 'bottom',
    },
  ],
};

// ─── 5. tour-colaboradores ────────────────────────────────────────────────────
// Disparo: automático em /colaboradores quando tourColaboradoresConcluido == false
//          OU quando tourColaboradoresConcluido == true mas tourColaboradoresComDadosConcluido == false e há dados
// Variantes por contexto — veja TourContext

export const TOUR_COLABORADORES: TourDefinition = {
  id: 'tour-colaboradores',
  flagConclusao: 'tourColaboradoresConcluido',
  visibleTo: ['*'],
  steps: [
    {
      route: '/pessoas',
      element: '[data-tour="times-header"]',
      title: '👥 Painel de Colaboradores',
      description:
        'Aqui você acompanha <strong>todos os seus colaboradores em tempo real</strong>. ' +
        'Selecione um time no campo abaixo para ver quem está online, ausente ou ' +
        'offline — tudo em um único lugar.',
      side: 'bottom',
    },
    // Step 2 sem times (variante "semTimes")
    // Step 2 com times (variante "comTimes")
    // Step 3 com times mas sem dados (variante "semDados")
    // Steps 4 e 5 com dados reais (variante "comDados")
  ],
  variantes: {
    semTimes: [
      {
        route: '/pessoas',
        element: '[data-tour="times-select-card"]',
        title: '⚠️ Nenhum time encontrado ainda',
        description:
          'Para ver seus colaboradores aqui, você precisa primeiro <strong>criar um ' +
          'time em Empresa</strong> e adicionar colaboradores a ele. Quando isso ' +
          'estiver feito, volte aqui e o painel estará populado.',
        side: 'bottom',
      },
    ],
    comTimes: [
      {
        route: '/pessoas',
        element: '[data-tour="times-select-card"]',
        title: '🔍 Selecione um time para começar',
        description:
          'Use esse campo para <strong>escolher qual time você quer visualizar</strong>. ' +
          'Cada time tem seu próprio painel com status, scores e indicadores dos membros. ' +
          'Você pode alternar entre times a qualquer momento.',
        side: 'bottom',
      },
    ],
    semDados: [
      {
        route: '/pessoas',
        element: '[data-tour="times-select-card"]',
        title: '⏳ Aguardando dados do agente',
        description:
          'O time está configurado, mas o <strong>agente ainda não enviou dados de ' +
          'atividade</strong>. Assim que os colaboradores estiverem com o agente ' +
          'instalado e ativo, os dados aparecem aqui automaticamente.',
        side: 'bottom',
      },
    ],
    comDados: [
      {
        route: '/pessoas',
        element: '[data-tour="colaboradores-lista"]',
        title: '📋 Seu time em detalhes',
        description:
          'Cada card de colaborador mostra o <strong>status atual, o iManager Score ' +
          'da semana e os indicadores de saúde</strong> como burnout e desengajamento. ' +
          'Clique em qualquer colaborador para ver o perfil completo dele.',
        side: 'top',
      },
      {
        route: '/pessoas',
        element: '[data-tour="colaboradores-lista"]',
        title: '🎯 Clique num colaborador para mergulhar fundo',
        description:
          'O painel individual mostra a <strong>linha do tempo de atividade, KPIs ' +
          'de produtividade e os indicadores psicológicos</strong> daquele colaborador. ' +
          'É onde você vai quando precisa entender o que está acontecendo com alguém ' +
          'do time.',
        side: 'top',
      },
    ],
  },
};

// ─── 6. tour-colaborador-detalhe ──────────────────────────────────────────────
// Disparo: automático em /pessoas/membro/:id quando tourColaboradorDetalheConcluido == false
// Pré-condição: deve existir pelo menos 1 semana de dados (verificar via temDados no contexto)

export const TOUR_COLABORADOR_DETALHE: TourDefinition = {
  id: 'tour-colaborador-detalhe',
  flagConclusao: 'tourColaboradorDetalheConcluido',
  visibleTo: ['*'],
  steps: [
    {
      route: '/pessoas/membro/:id',
      element: '[data-tour="detalhe-kpi-cards"]',
      title: '📋 Acompanhe o dia em tempo real',
      description:
        'Jornada total, tempo ativo, pausas e ausências — <strong>tudo atualizado ' +
        'conforme o agente envia dados</strong>. Use as setas para navegar entre os dias.',
      side: 'bottom',
    },
    {
      route: '/pessoas/membro/:id',
      element: '[data-tour="detalhe-timeline-visual"]',
      title: '📊 A timeline do dia',
      description:
        'Visualize como foi o dia: <strong>blocos de atividade, pausas e períodos ' +
        'offline</strong> em uma linha do tempo. Gaps frequentes podem indicar interrupções.',
      side: 'top',
    },
    {
      route: '/pessoas/membro/:id',
      element: '[data-tour="detalhe-frase-acionavel"]',
      title: '💡 Diagnóstico automático',
      description:
        'Uma frase gerada automaticamente <strong>resume como está sendo o dia</strong>, ' +
        'com base nos dados reais — jornada excedida, tempo ausente alto ou dia saudável.',
      side: 'top',
    },
    {
      route: '/pessoas/membro/:id',
      element: '[data-tour="detalhe-resumo"]',
      title: '📈 Padrões ao longo do tempo',
      description:
        'Mude para a <strong>aba Resumo</strong> para ver médias, tendências de jornada ' +
        'e sessões de foco do período. Compare com a média do time.',
      side: 'top',
    },
    {
      route: '/pessoas/membro/:id',
      element: '[data-tour="detalhe-tendencias"]',
      title: '🎯 Tendências e indicadores',
      description:
        'Os gráficos de <strong>Jornada por Dia</strong> e <strong>Sessões de Foco</strong> ' +
        'mostram se o ritmo é saudável ou se há sinais de alerta. As cores indicam desvios.',
      side: 'top',
    },
  ],
};

// ─── 7. tour-relatorios ───────────────────────────────────────────────────────
// Disparo: automático em /relatorios quando tourRelatoriosConcluido == false
// Variante: step 4 (relatorios-cards-area) só aparece quando temDados == true

export const TOUR_RELATORIOS: TourDefinition = {
  id: 'tour-relatorios',
  flagConclusao: 'tourRelatoriosConcluido',
  visibleTo: ['*'],
  steps: [
    {
      route: '/relatorios',
      element: '[data-tour="relatorios-header"]',
      title: '📊 Relatórios Semanais por IA',
      description:
        'Esta é a seção mais poderosa do Manager. Toda semana, a <strong>IA gera ' +
        'um diagnóstico completo por colaborador e por time</strong> — o que aconteceu, ' +
        'quem precisa de atenção e o que você pode fazer como gestor.',
      side: 'bottom',
    },
    {
      route: '/relatorios',
      element: '[data-tour="relatorios-filtro-time"]',
      title: '🔍 Filtre por time ou colaborador',
      description:
        'Escolha <strong>qual time você quer analisar</strong>. Depois clique em ' +
        'qualquer colaborador do time para ver o relatório individual dele. O Manager ' +
        'organiza automaticamente os dados por hierarquia.',
      side: 'bottom',
    },
    {
      route: '/relatorios',
      element: '[data-tour="relatorios-semana-nav"]',
      title: '📅 Navegue pelas semanas anteriores',
      description:
        'Use as setas para <strong>acessar relatórios de semanas passadas</strong>. ' +
        'O Manager guarda o histórico completo para você comparar evolução e ' +
        'acompanhar tendências ao longo do tempo.',
      side: 'bottom',
    },
  ],
  variantes: {
    comDados: [
      {
        route: '/relatorios',
        element: '[data-tour="relatorios-cards-area"]',
        title: '🤖 O diagnóstico completo da semana',
        description:
          'Cada card traz o <strong>iManager Score, os 6 pilares de desempenho, ' +
          'os indicadores psicológicos e as recomendações da IA</strong> para aquele ' +
          'colaborador. É a visão que o gestor precisa para tomar decisões com ' +
          'confiança — não intuição.',
        side: 'top',
      },
    ],
  },
};

// ─── 8. tour-perfil ───────────────────────────────────────────────────────────
// Disparo: automático em /configuracoes/perfil quando tourPerfilConcluido == false

export const TOUR_PERFIL: TourDefinition = {
  id: 'tour-perfil',
  flagConclusao: 'tourPerfilConcluido',
  visibleTo: ['*'],
  steps: [
    {
      route: '/configuracoes/perfil',
      element: '[data-tour="perfil-card"]',
      title: '👤 Suas informações pessoais',
      description:
        'Aqui ficam os seus dados de perfil — <strong>nome, e-mail e informações ' +
        'da sua conta</strong>. Mantenha tudo atualizado para garantir que as ' +
        'notificações e relatórios cheguem corretamente.',
      side: 'bottom',
    },
    {
      route: '/configuracoes/perfil',
      element: '[data-tour="configuracoes-tours"]',
      title: '🔁 Você pode rever qualquer tour',
      description:
        'Em <strong>Configurações</strong>, na seção "Tours e Onboarding", você ' +
        'encontra a lista de todos os tours disponíveis e pode rever qualquer um ' +
        'deles a qualquer momento. O ícone <strong>?</strong> no topo de cada ' +
        'página também funciona para isso.',
      side: 'top',
    },
  ],
};

// ─── Mapa e lista completa ────────────────────────────────────────────────────

export const TOUR_DEFINITIONS_MAP: Record<string, TourDefinition> = {
  'tour-home': TOUR_HOME,
  'tour-org-estrutura': TOUR_ORG_ESTRUTURA,
  'tour-org-times': TOUR_ORG_TIMES,
  'tour-org-hierarquia': TOUR_ORG_HIERARQUIA,
  'tour-colaboradores': TOUR_COLABORADORES,
  'tour-colaborador-detalhe': TOUR_COLABORADOR_DETALHE,
  'tour-relatorios': TOUR_RELATORIOS,
  'tour-perfil': TOUR_PERFIL,
};

export const ALL_TOURS: TourDefinition[] = Object.values(TOUR_DEFINITIONS_MAP);
